import useToken from 'hooks/useToken';
import React, { useEffect, useState, useRef, useContext } from 'react';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Button } from 'react-bootstrap';
import { Card, Col, Row, Form, InputGroup, Spinner } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faTimes,
    faFileInvoice,
    faFilter,
    faCalculator
} from '@fortawesome/free-solid-svg-icons';

import ModalCalcularEmitidas from './ModalCalcularEmitidas';
import FacturaCard from './FacturaCard';
import { useToast } from 'context/providers/ToastProvider';

import { ContabilidadLayout } from 'akasia-components/Contabilidad/common/ContabilidadLayout';
import { ContabilidadContext } from 'akasia-components/Contabilidad/common/ContabilidadContext';

const ListaFacturasEmitidasContent = () => {
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();
    const { selectedCompany, globalDateQuarter: dateQuarter, setGlobalDateQuarter: setDateQuarter } = useContext(ContabilidadContext);
    const [emitidas, setEmitidas] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [busqueda, setBusqueda] = useState('');

    // Nuevos estados para filtros adicionales
    const [filtroSerie, setFiltroSerie] = useState('');
    const [filtroFormaPago, setFiltroFormaPago] = useState('');
    const [filtroMetodoPago, setFiltroMetodoPago] = useState('');
    const [filtroTipoComprobante, setFiltroTipoComprobante] = useState('');
    const [filtroEstadoComprobante, setFiltroEstadoComprobante] = useState('');
    const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

    // Estados para infinite scroll
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(20);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    // Estado para facturas seleccionadas
    const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);

    // Estado para formas de pago
    const [formasPago, setFormasPago] = useState([]);
    const [cargandoFormasPago, setCargandoFormasPago] = useState(false);

    // Ref para controlar si es la primera carga
    const debounceTimer = useRef(null);

    /**
    * Se ejecuta la primera vez
    */
    useEffect(() => {
        obtenerFormasPago();
    }, []);

    /**
     * Función para obtener las formas de pago desde el endpoint
     */
    const obtenerFormasPago = async () => {
        setCargandoFormasPago(true);
        try {
            const response = await fetch("/api/contabilidad/CatalogoSAT/ObtenerFormasDePago", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Respuesta de formas de pago:', data);

                if (data.iResponseType === 1) {
                    const detalles = JSON.parse(data.detalles);
                    console.log('Detalles parseados:', detalles);

                    setFormasPago(detalles.table2);
                } else {
                    console.error('Error en respuesta:', data.vMessage);
                    setFormasPago([]);
                    showErrorToast(data.vMessage || 'Error al cargar formas de pago');
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                } else {
                    showErrorToast(response.status + ' ' + response.statusText);
                }
                setFormasPago([]);
            }
        } catch (error) {
            console.error('Error al obtener formas de pago:', error);
            setFormasPago([]);
            showErrorToast('Hubo un problema al obtener formas de pago: ' + error);
        } finally {
            setCargandoFormasPago(false);
        }
    };

    /**
    * Efecto unificado para cambios en filtros
    */
    useEffect(() => {
        // Solo ejecutar si hay un RFC seleccionado
        if (!selectedCompany?.vRFC) {
            setEmitidas([]);
            setTotalRecords(0);
            return;
        }

        // Limpiar timeout anterior
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Si hay búsqueda, aplicar debounce
        if (busqueda !== '') {
            debounceTimer.current = setTimeout(() => {
                resetAndFetch();
            }, 500);
        } else {
            // Si no hay búsqueda, ejecutar inmediatamente
            resetAndFetch();
        }

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [selectedCompany?.vRFC, dateQuarter, busqueda, filtroSerie, filtroFormaPago, filtroMetodoPago, filtroTipoComprobante, filtroEstadoComprobante]);

    /**
     * Función para resetear y obtener datos
     */
    const resetAndFetch = () => {
        setPageNumber(1);
        setHasMoreData(true);
        setEmitidas([]);
        setFacturasSeleccionadas([]);
        informacionFactura(1, false);
    };

    /**
     * Función para limpiar todos los filtros
     */
    const limpiarFiltros = () => {
        setFiltroSerie('');
        setFiltroFormaPago('');
        setFiltroMetodoPago('');
        setFiltroTipoComprobante('');
        setFiltroEstadoComprobante('');
        setBusqueda('');
    };

    /**
     * Función para manejar la selección/deselección de facturas
     */
    const toggleSeleccionFactura = (idFactura) => {
        setFacturasSeleccionadas(prev => {
            if (prev.includes(idFactura)) {
                return prev.filter(id => id !== idFactura);
            } else {
                return [...prev, idFactura];
            }
        });
    };

    // obtenerRfcReceptor e informacionFacturaInicial han sido eliminados a favor de estado global y fetchData unificado

    // Maneja el cambio de fecha
    const handleDateChange = (date) => {
        setDateQuarter(date);
    };

    /**
    * Función para mostrar datos de facturas con paginación
    */
    const informacionFactura = async (page = 1, append = false) => {
        if (!append) {
            setCargando(true);
        } else {
            setLoadingMore(true);
        }

        var date_ = dateQuarter;
        const fechaActual = new Date(date_);

        var month = fechaActual.getMonth() + 1;
        var year = fechaActual.getFullYear();
        var formattedMonth = month < 10 ? '0' + month : '' + month;

        const data = {
            year: '' + year,
            month: formattedMonth,
            rfc: selectedCompany?.vRFC || '',
            pageNumber: page,
            pageSize: pageSize,
            searchTerm: busqueda,
            serie: filtroSerie,
            formaPago: filtroFormaPago,
            metodoPago: filtroMetodoPago,
            tipoComprobante: filtroTipoComprobante,
            estadoComprobante: filtroEstadoComprobante
        };

        const response = await fetch(
            "/api/contabilidad/centro-xml/FacturasEmitidas/InformacionFacturaEmitida",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la petición Fetch:' + error);
            });

        if (response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);
                const nuevasFacturas = ObjDetalles.table2 || [];

                if (append) {
                    setEmitidas(prev => [...prev, ...nuevasFacturas]);
                } else {
                    setEmitidas(nuevasFacturas);
                    setPageNumber(1);
                }

                const totalRecordsFromResponse = ObjDetalles.table3?.[0]?.totalRecords || nuevasFacturas.length;
                setTotalRecords(totalRecordsFromResponse);
                setHasMoreData((page * pageSize) < totalRecordsFromResponse);
            } else {
                showErrorToast(data.vMessage);
                if (!append) {
                    setEmitidas([]);
                    setHasMoreData(false);
                }
            }
        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
            if (!append) {
                setEmitidas([]);
                setHasMoreData(false);
            }
        }

        if (!append) {
            setCargando(false);
        } else {
            setLoadingMore(false);
        }
    };

    /**
     * Función para cargar más datos (infinite scroll)
     */
    const cargarMasDatos = () => {
        if (hasMoreData && !loadingMore && !cargando) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            informacionFactura(nextPage, true);
        }
    };

    /**
     * Componente para el trigger de infinite scroll
     */
    const InfiniteScrollTrigger = () => {
        const triggerRef = React.useRef();

        React.useEffect(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreData && !loadingMore && !cargando) {
                        cargarMasDatos();
                    }
                },
                { threshold: 0.1 }
            );

            if (triggerRef.current) {
                observer.observe(triggerRef.current);
            }

            return () => {
                if (triggerRef.current) {
                    observer.unobserve(triggerRef.current);
                }
            };
        }, [hasMoreData, loadingMore, cargando]);

        if (!hasMoreData) return null;

        return (
            <div ref={triggerRef} className="text-center py-4">
                {loadingMore ? (
                    <div>
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted small">Cargando más facturas...</div>
                    </div>
                ) : (
                    <div className="text-muted small">Desplázate para cargar más</div>
                )}
            </div>
        );
    };

    return (
        <>
            <Row>
                <Col>
                    <Card className="mb-3">
                        <Card.Header className="bg-light">
                            <Row className="align-items-end g-2">
                                {/* Grupo DatePicker */}
                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label className="text-700 mb-1">Mes</Form.Label>
                                        <DatePicker
                                            selected={dateQuarter}
                                            onChange={handleDateChange}
                                            dateFormat="MMMM yyyy - QQQ"
                                            className="form-control w-100"
                                            showMonthYearPicker
                                        />
                                    </Form.Group>
                                </Col>



                                {/* Buscador */}
                                <Col xs={12} md={4}>
                                    <Form.Group>
                                        <Form.Label className="text-700 mb-1">Buscar facturas</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FontAwesomeIcon icon={faSearch} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Buscar por folio, RFC, razón social..."
                                                value={busqueda}
                                                onChange={(e) => setBusqueda(e.target.value)}
                                            />
                                            {busqueda && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setBusqueda('')}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Form.Group>
                                </Col>

                                {/* Botón Filtros Avanzados */}
                                <Col xs={3} md={1}>
                                    <Button
                                        variant={mostrarFiltrosAvanzados ? 'primary' : 'outline-primary'}
                                        onClick={() => setMostrarFiltrosAvanzados(!mostrarFiltrosAvanzados)}
                                        className="w-100"
                                    >
                                        <FontAwesomeIcon icon={faFilter} className="me-1" />

                                    </Button>
                                </Col>

                                {/* Botón Cálculo */}
                                <Col xs={6} md="auto" className="ms-auto d-flex justify-content-end">
                                    <Button
                                        variant="success"
                                        onClick={() => setShowModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faCalculator} className="me-2" />
                                        Cálculo
                                    </Button>
                                </Col>
                            </Row>

                            {/* Filtros Avanzados */}
                            {mostrarFiltrosAvanzados && (
                                <Row className="mt-3 g-2">
                                    <Col xs={12} sm={6} md={2}>
                                        <Form.Group>
                                            <Form.Label className="text-700 mb-1">Serie</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Filtrar por serie..."
                                                value={filtroSerie}
                                                onChange={(e) => setFiltroSerie(e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12} sm={6} md={2}>
                                        <Form.Group>
                                            <Form.Label className="text-700 mb-1">Forma de Pago</Form.Label>
                                            <Form.Select
                                                value={filtroFormaPago}
                                                onChange={(e) => setFiltroFormaPago(e.target.value)}
                                                size="sm"
                                                disabled={cargandoFormasPago}
                                            >
                                                <option value="">Todas</option>
                                                {Array.isArray(formasPago) && formasPago.map((fp) => (
                                                    <option key={fp.idCatFormaPago} value={fp.vClave}>
                                                        {fp.vClave} - {fp.vDescripcionFormaPago}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12} sm={6} md={2}>
                                        <Form.Group>
                                            <Form.Label className="text-700 mb-1">Método de Pago</Form.Label>
                                            <Form.Select
                                                value={filtroMetodoPago}
                                                onChange={(e) => setFiltroMetodoPago(e.target.value)}
                                                size="sm"
                                            >
                                                <option value="">Todos</option>
                                                <option value="PUE">PUE</option>
                                                <option value="PPD">PPD</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12} sm={6} md={2}>
                                        <Form.Group>
                                            <Form.Label className="text-700 mb-1">Tipo de Comprobante</Form.Label>
                                            <Form.Select
                                                value={filtroTipoComprobante}
                                                onChange={(e) => setFiltroTipoComprobante(e.target.value)}
                                                size="sm"
                                            >
                                                <option value="">Todos</option>
                                                <option value="I">I - Ingreso</option>
                                                <option value="E">E - Egreso</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12} sm={6} md={2}>
                                        <Form.Group>
                                            <Form.Label className="text-700 mb-1">Estado</Form.Label>
                                            <Form.Select
                                                value={filtroEstadoComprobante}
                                                onChange={(e) => setFiltroEstadoComprobante(e.target.value)}
                                                size="sm"
                                            >
                                                <option value="">Todos</option>
                                                <option value="1">Activos</option>
                                                <option value="0">Cancelados</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12} sm={6} md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="outline-danger"
                                            onClick={limpiarFiltros}
                                            className="w-100"
                                            size="sm"
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                                            Limpiar Filtros
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </Card.Header>

                        <Card.Body className="p-0">
                            {cargando ? (
                                <div className="p-4">
                                    {[...Array(5)].map((_, index) => (
                                        <Card key={index} className="mb-3">
                                            <Card.Body>
                                                <Row>
                                                    <Col xs={12} md={2}>
                                                        <Skeleton height={30} width={80} className="mb-2" />
                                                        <Skeleton height={15} width={60} />
                                                    </Col>
                                                    <Col xs={12} md={6}>
                                                        <Skeleton height={20} className="mb-2" />
                                                        <Skeleton height={15} width={200} />
                                                        <Skeleton height={15} width={150} />
                                                    </Col>
                                                    <Col xs={12} md={4} className="text-end">
                                                        <Skeleton height={25} width={100} className="mb-2" />
                                                        <Skeleton height={20} width={60} />
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    ))}

                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : emitidas.length > 0 ? (
                                <div className="p-3">
                                    <div className="mb-3 d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                            Mostrando {emitidas.length} de {totalRecords} facturas
                                            {busqueda && ` para "${busqueda}"`}
                                        </small>
                                        {facturasSeleccionadas.length > 0 && (
                                            <small className="text-primary fw-bold">
                                                {facturasSeleccionadas.length} factura{facturasSeleccionadas.length !== 1 ? 's' : ''} seleccionada{facturasSeleccionadas.length !== 1 ? 's' : ''}
                                            </small>
                                        )}
                                    </div>
                                    {emitidas.map((factura) => (
                                        <FacturaCard
                                            key={factura.idFacturaEmitida}
                                            factura={factura}
                                            isSelected={facturasSeleccionadas.includes(factura.idFacturaEmitida)}
                                            onToggleSelection={toggleSeleccionFactura}
                                        />
                                    ))}
                                    <InfiniteScrollTrigger />
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <div className="mb-3">
                                        <FontAwesomeIcon
                                            icon={faFileInvoice}
                                            size="3x"
                                            className="text-muted"
                                        />
                                    </div>
                                    <h5 className="text-muted">No se encontraron facturas</h5>
                                    <p className="text-muted">
                                        {busqueda
                                            ? `No hay facturas que coincidan con "${busqueda}"`
                                            : 'No hay facturas registradas para el periodo seleccionado'
                                        }
                                    </p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal */}
            {showModal && (
                <ModalCalcularEmitidas
                    showModal={showModal}
                    setShowModal={setShowModal}
                    dateQuarter={dateQuarter}
                    selectedRfc={selectedCompany?.vRFC}
                    searchTerm={busqueda}
                    serie={filtroSerie}
                    formaPago={filtroFormaPago}
                    metodoPago={filtroMetodoPago}
                    tipoComprobante={filtroTipoComprobante}
                />
            )}
        </>
    );
};

const ListaFacturasEmitidas = () => {
    return (
        <ContabilidadLayout
            title={
                <><FontAwesomeIcon icon={faFileInvoice} className="me-2" />Facturas Emitidas</>
            }
        >
            <ListaFacturasEmitidasContent />
        </ContabilidadLayout>
    );
};

export default ListaFacturasEmitidas;

